import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherX } from "@subframe/core";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import {
  useToast,
  GlAccountTypePicker,
  GlParentAccountDropdown,
} from "../../../../shared/presentation/components";
import { getStoredAccountingBackend } from "../../../../config/accounting";
import { useGls } from "../hooks/useGls";
import type { GlEntity } from "../../domain/entities/GlEntity";
import { getAccountNameLeaf } from "../../../../utils";

/** Account types hidden from the GL form dropdown (management view excludes these from list). */
const EXCLUDED_ACCOUNT_TYPES = [
  "bank",
  "accounts_receivable",
  "accounts_payable",
];

export type GlFormSuccessParams =
  | { mode: "create"; sentForReview: boolean }
  | { mode: "edit"; sentForReview: false; gl: GlEntity }
  | { mode: "edit"; sentForReview: true; accountId: string };

export interface GlFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (params: GlFormSuccessParams) => void;
  mode: "create" | "edit";
  initialGl?: GlEntity | null;
}

export const GlFormModal: React.FC<GlFormModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  mode,
  initialGl,
}) => {
  const { getPermissionInfo } = usePermissions();
  const { showSuccess, showError } = useToast();
  const {
    createGl,
    updateGl,
    getAccountTypeGroups,
    accountTypeGroups,
    accountTypeGroupsLoading,
  } = useGls();

  const actionCode =
    mode === "create" ? ACTION_CODES.CREATE : ACTION_CODES.EDIT;
  const { granted, requiresReview } = getPermissionInfo(
    MODULE_CODES.GL,
    actionCode,
  );

  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [isSubAccount, setIsSubAccount] = useState(false);
  const [parentAccountId, setParentAccountId] = useState<string | null>(null);
  const [parentAccountName, setParentAccountName] = useState<string>("");
  const [parentAccountFullPath, setParentAccountFullPath] =
    useState<string>("");
  const [invoice, setInvoice] = useState(false);
  const [bill, setBill] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<
    "create" | "edit" | "delete"
  >("create");

  const accountTypeGroupMap = accountTypeGroups?.groups ?? {};
  const accountTypeLabels = accountTypeGroups?.labels ?? {};
  const isTally = getStoredAccountingBackend() === "tally";
  /** Zoho parents (has_children): bill/invoice are rollup-owned — hide usage edits. Reparenting is allowed. */
  const isParentAccount =
    mode === "edit" && Boolean(initialGl?.hasChildren);

  const resetForm = useCallback(() => {
    if (mode === "edit" && initialGl) {
      setAccountName(initialGl.glName);
      setAccountType(initialGl.accountType);
      setIsSubAccount(!!initialGl.parentId);
      setParentAccountId(initialGl.parentId ?? null);
      setParentAccountName(
        initialGl.parentName ? getAccountNameLeaf(initialGl.parentName) : "",
      );
      setParentAccountFullPath(initialGl.parentName ?? "");
      setInvoice(initialGl.invoice);
      setBill(initialGl.bill);
    } else {
      setAccountName("");
      setAccountType("");
      setIsSubAccount(false);
      setParentAccountId(null);
      setParentAccountName("");
      setParentAccountFullPath("");
      setInvoice(false);
      setBill(false);
    }
    setErrors({});
  }, [mode, initialGl]);

  useEffect(() => {
    if (open) {
      resetForm();
      getAccountTypeGroups();
    }
  }, [open, resetForm, getAccountTypeGroups]);

  const handleClose = () => {
    if (!isSubmitting) {
      setErrors({});
      onOpenChange(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!accountName?.trim())
      newErrors.accountName = "Account name is required";
    if (!accountType) newErrors.accountType = "Account type is required";
    if (isSubAccount && !parentAccountId)
      newErrors.parentAccountId = isTally
        ? "Parent group is required when creating a sub account"
        : "Parent account is required when creating a sub account";
    if (!isParentAccount && !invoice && !bill)
      newErrors.usage =
        "At least one of Use in Invoices or Use in Bills must be selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildChangeDetails = (action: "create" | "edit" | "delete") => {
    const raw: Record<string, unknown> = {
      account_name: accountName.trim(),
      account_type: accountType,
      parent_account_id: isSubAccount ? parentAccountId : null,
      parent_account_name:
        parentAccountFullPath || parentAccountName || undefined,
      invoice,
      bill,
      node_kind: "ledger",
    };
    if (mode === "edit" && initialGl) raw.account_id = initialGl.accountId;
    const changeDetails: {
      action_types: string[];
      raw_json: Record<string, unknown>;
      field_changes?: Array<{ field: string; old: string; new: string }>;
    } = {
      action_types: [action],
      raw_json: raw,
    };
    if (action === "edit" && initialGl) {
      const fc: Array<{ field: string; old: string; new: string }> = [];
      if (initialGl.glName !== accountName.trim())
        fc.push({
          field: "account_name",
          old: initialGl.glName,
          new: accountName.trim(),
        });
      if (initialGl.accountType !== accountType)
        fc.push({
          field: "account_type",
          old: initialGl.accountType,
          new: accountType,
        });
      if ((initialGl.parentId ?? null) !== parentAccountId)
        fc.push({
          field: "parent_account_id",
          old: initialGl.parentName || "None",
          new:
            isSubAccount && parentAccountFullPath
              ? parentAccountFullPath
              : isSubAccount && parentAccountName
                ? parentAccountName
                : "None",
        });
      if (initialGl.invoice !== invoice)
        fc.push({
          field: "invoice",
          old: String(initialGl.invoice),
          new: String(invoice),
        });
      if (initialGl.bill !== bill)
        fc.push({
          field: "bill",
          old: String(initialGl.bill),
          new: String(bill),
        });
      changeDetails.field_changes = fc;
    }
    return changeDetails;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (requiresReview) {
      setReviewAction(mode === "create" ? "create" : "edit");
      setIsReviewModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        account_name: accountName.trim(),
        account_type: accountType,
        parent_account_id: isSubAccount ? parentAccountId : null,
        invoice,
        bill,
        node_kind: "ledger" as const,
      };
      if (mode === "create") {
        await createGl(payload);
        showSuccess("GL created successfully");
        onSuccess?.({ mode: "create", sentForReview: false });
      } else if (initialGl) {
        await updateGl(initialGl.accountId, payload);
        showSuccess("GL updated successfully");
        // Build updated entity from form state so the list row updates instantly
        const updatedGl: GlEntity = {
          ...initialGl,
          glName: accountName.trim(),
          accountType,
          parentId: isSubAccount ? parentAccountId : null,
          parentName: isSubAccount
            ? parentAccountFullPath || parentAccountName || null
            : null,
          invoice,
          bill,
        };
        onSuccess?.({ mode: "edit", sentForReview: false, gl: updatedGl });
      }
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Operation failed";
      showError(message);
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex max-h-[90vh] w-144 flex-col items-start gap-6 overflow-hidden bg-white px-8 py-8">
          {/* Header */}
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {mode === "create" ? "Create GL" : "Edit GL"}
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          {/* Form fields */}
          <div className="flex w-full flex-1 flex-col items-start gap-5 overflow-y-auto pr-1">
            {errors.submit && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {errors.submit}
                </span>
              </div>
            )}

            {/* Account name */}
            <div className="flex w-full flex-col gap-1">
              <TextField
                className="h-auto w-full flex-none"
                variant="filled"
                label="Account name *"
                helpText="Display name for this account"
                error={!!errors.accountName}
              >
                <TextField.Input
                  placeholder="e.g. Cash"
                  value={accountName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setAccountName(e.target.value);
                    if (errors.accountName)
                      setErrors((prev) => ({ ...prev, accountName: "" }));
                  }}
                />
              </TextField>
              {errors.accountName && (
                <span className="text-caption font-caption text-error-700">
                  {errors.accountName}
                </span>
              )}
            </div>

            {/* Account type */}
            <div className="flex w-full flex-col gap-1">
              <span className="text-body-bold font-body-bold text-default-font">
                Account type <span className="text-error-600">*</span>
              </span>
              <GlAccountTypePicker
                groups={accountTypeGroupMap}
                labels={accountTypeLabels}
                selectedAccountType={accountType || undefined}
                excludedAccountTypes={EXCLUDED_ACCOUNT_TYPES}
                loading={accountTypeGroupsLoading}
                onSelect={(nextType) => {
                  setAccountType(nextType);
                  setParentAccountId(null);
                  setParentAccountName("");
                  setParentAccountFullPath("");
                  if (errors.accountType) {
                    setErrors((prev) => ({ ...prev, accountType: "" }));
                  }
                }}
                placeholder="Select account type"
              />
              {errors.accountType && (
                <span className="text-caption font-caption text-error-700">
                  {errors.accountType}
                </span>
              )}
            </div>

            {/* Is sub account? */}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isSubAccount}
                onChange={(e) => {
                  setIsSubAccount(e.target.checked);
                  if (!e.target.checked) {
                    setParentAccountId(null);
                    setParentAccountName("");
                    setParentAccountFullPath("");
                  }
                  if (errors.parentAccountId)
                    setErrors((prev) => ({ ...prev, parentAccountId: "" }));
                }}
                className="h-4 w-4 rounded border-neutral-300 accent-brand-600"
              />
              <span className="text-body font-body text-default-font">
                {isTally ? "Place under a group?" : "Is sub account?"}
              </span>
            </label>

            {/* Parent account — only visible when isSubAccount */}
            {isSubAccount && (
              <div className="flex w-full flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  {isTally ? "Parent group" : "Parent account"}
                </span>
                {isTally && (
                  <span className="text-caption font-caption text-subtext-color">
                    Only groups of the selected account type are shown.
                  </span>
                )}
                {!isTally && isParentAccount && (
                  <span className="text-caption font-caption text-subtext-color">
                    Moving this account also moves its children. Its own
                    descendants are hidden from this list.
                  </span>
                )}
                <GlParentAccountDropdown
                  accountType={accountType}
                  selectedAccountId={parentAccountId ?? undefined}
                  selectedAccountName={parentAccountName || undefined}
                  excludeAccountId={
                    mode === "edit" && initialGl
                      ? initialGl.accountId
                      : undefined
                  }
                  disabled={!accountType}
                  onSelect={(account) => {
                    setParentAccountId(account.account_id);
                    setParentAccountName(account.gl_name);
                    setParentAccountFullPath(
                      account.full_path ?? account.gl_name,
                    );
                    if (errors.parentAccountId) {
                      setErrors((prev) => ({ ...prev, parentAccountId: "" }));
                    }
                  }}
                  placeholder={
                    accountType
                      ? isTally
                        ? "Select parent group"
                        : "Select parent account"
                      : "Select an account type first"
                  }
                />
                {errors.parentAccountId && (
                  <span className="text-caption font-caption text-error-700">
                    {errors.parentAccountId}
                  </span>
                )}
              </div>
            )}

            {/* Use in Invoices + Use in Bills — side by side, at least one required */}
            {!isParentAccount && (
              <div className="flex w-full flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  Usage <span className="text-error-600">*</span>
                </span>
                <div className="flex w-full items-center gap-6">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={invoice}
                      onChange={(e) => {
                        setInvoice(e.target.checked);
                        if (errors.usage)
                          setErrors((prev) => ({ ...prev, usage: "" }));
                      }}
                      className="h-4 w-4 rounded border-neutral-300 accent-brand-600"
                    />
                    <span className="text-body font-body text-default-font">
                      Use in Invoices
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={bill}
                      onChange={(e) => {
                        setBill(e.target.checked);
                        if (errors.usage)
                          setErrors((prev) => ({ ...prev, usage: "" }));
                      }}
                      className="h-4 w-4 rounded border-neutral-300 accent-brand-600"
                    />
                    <span className="text-body font-body text-default-font">
                      Use in Bills
                    </span>
                  </label>
                </div>
                {errors.usage && (
                  <span className="text-caption font-caption text-error-700">
                    {errors.usage}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
            <Button
              variant="neutral-tertiary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !granted}
              loading={isSubmitting}
            >
              {requiresReview
                ? "Submit for review"
                : mode === "create"
                  ? "Create"
                  : "Update"}
            </Button>
          </div>
        </div>
      </DialogLayout>

      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode="gl"
        actionCode={reviewAction === "create" ? "create" : "edit"}
        entityType="gl"
        entityId={mode === "edit" && initialGl ? initialGl.id : 0}
        changeDetails={buildChangeDetails(reviewAction)}
        skipNavigation={true}
        onSuccess={() => {
          setIsReviewModalOpen(false);
          if (mode === "edit" && initialGl) {
            onSuccess?.({
              mode: "edit",
              sentForReview: true,
              accountId: initialGl.accountId,
            });
          } else {
            onSuccess?.({ mode: "create", sentForReview: true });
          }
          onOpenChange(false);
        }}
      />
    </>
  );
};
